// src/lib/issueImport.js
// Saves issues fetched from Jira into the issues table. Re-importing updates the
// issue already stored for that Jira key instead of adding a duplicate, and keeps
// the triage done in SPCC (status, tier, priority, assignee).
import { autoClassify } from './triage'
import { issuesService, batchDataChanges } from '../services/database'

const TIER = { tier1: 1, tier2: 2 }
const LEVEL = { tier1: 'critical', tier2: 'high' }

export function toIssueRecord(r) {
  const tier = autoClassify(r)
  const now = new Date().toISOString()
  return {
    external_id: r.ext_key || r.id,
    title: r.title,
    description: r.summary || r.description || '',
    status: 'new',
    priority: LEVEL[tier] || 'medium',
    severity: LEVEL[tier] || 'medium',
    tier: TIER[tier] || 3,
    source: r.source || 'jira',
    assignee: r.assignee || r.owner || null,
    labels: r.tags || [],
    external_url: r.url || null,
    triage_notes: r.impact ? `Impact: ${r.impact}` : null,
    created_date: r.created_date || now,
    updated_date: r.updated_date || now,
  }
}

// Fields that follow Jira on re-import; everything else is SPCC's own triage.
const SYNCED_FIELDS = ['title', 'description', 'external_url', 'labels', 'updated_date']
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

async function forEachLimited(items, limit, fn) {
  let next = 0
  const worker = async () => { while (next < items.length) { const i = next++; await fn(items[i], i) } }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
}

// One Jira issue = one key, whether it was stored as "jira:PROD-1" or "PROD-1".
const jiraKey = (issue) => {
  const key = issue.external_id || issue.ext_key
  if (!key || !(/^jira:/i.test(key) || /^jira$/i.test(issue.source || ''))) return null
  return String(key).replace(/^jira:/i, '')
}

// Which copy of a duplicated issue survives: the one the user has worked on
// (triaged/resolved, then assigned), then the most recently changed.
const keepScore = (issue) => [
  issue.status && issue.status !== 'new' ? 1 : 0,
  issue.assignee || issue.owner ? 1 : 0,
  issue.updated_at || issue.updated_date || '',
]
const better = (a, b) => {
  const [sa, sb] = [keepScore(a), keepScore(b)]
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return sa[i] > sb[i] ? a : b
  return a
}

// Earlier imports created a new copy of an issue on every import. Returns the
// issues to keep (one per Jira key) and the ids of the extra copies.
export function findDuplicateIssues(issues) {
  const keep = new Map()
  for (const issue of issues) {
    const key = jiraKey(issue)
    if (!key) continue
    keep.set(key, keep.has(key) ? better(keep.get(key), issue) : issue)
  }
  const kept = new Set([...keep.values()].map(i => i.id))
  const duplicateIds = issues.filter(i => jiraKey(i) && !kept.has(i.id)).map(i => i.id)
  return { byKey: keep, duplicateIds }
}

export async function removeDuplicateIssues(issues, { service = issuesService, concurrency = 8 } = {}) {
  const { duplicateIds } = findDuplicateIssues(issues)
  if (!duplicateIds.length) return []
  const removed = []
  await batchDataChanges(() => forEachLimited(duplicateIds, concurrency, async (id) => {
    try { await service.delete(id); removed.push(id) }
    catch (error) { console.error('Failed to remove duplicate issue', id, error) }
  }))
  return removed
}

// rows: normalized Jira issues (jiraAdapter). Returns { created, updated, unchanged, failed, removed }.
export async function saveImportedIssues(rows, { service = issuesService, onProgress, concurrency = 8 } = {}) {
  const counts = { created: 0, updated: 0, unchanged: 0, failed: 0, removed: 0 }
  const stored = await service.list('-updated_date')
  counts.removed = (await removeDuplicateIssues(stored, { service, concurrency })).length
  const existing = findDuplicateIssues(stored).byKey

  // Jira can return the same key twice across pages; keep the last copy.
  const records = [...new Map(rows.map(r => toIssueRecord(r)).map(rec => [rec.external_id, rec])).values()]
  let done = 0
  await batchDataChanges(() => forEachLimited(records, concurrency, async (record) => {
    try {
      const current = existing.get(jiraKey(record))
      if (!current) {
        await service.create(record)
        counts.created++
      } else {
        const patch = Object.fromEntries(SYNCED_FIELDS.filter(f => !same(current[f], record[f])).map(f => [f, record[f]]))
        if (Object.keys(patch).length) { await service.update(current.id, patch); counts.updated++ }
        else counts.unchanged++
      }
    } catch (error) {
      counts.failed++
      console.error('Failed to save imported issue', record.external_id, error)
    }
    onProgress?.(++done, records.length)
  }))
  return counts
}
